import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, User, AlertCircle } from "lucide-react";
import Button from "./Button";
import { RoleEnum } from "../../modules/shared/model";
import { useTranslation } from "react-i18next";

interface ProfileCompletionDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onGoToProfile: () => void;
    userRole?: number;
}

const ProfileCompletionDialog: React.FC<ProfileCompletionDialogProps> = ({
    isOpen,
    onClose,
    onGoToProfile,
    userRole
}) => {
    const { t } = useTranslation();
    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4 md:p-6"
            >
                <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.95, opacity: 0 }}
                    className="bg-white rounded-xl max-w-sm sm:max-w-md md:max-w-lg w-full p-4 sm:p-5 md:p-6 shadow-xl max-h-[90vh] overflow-y-auto"
                >
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4 sm:mb-5">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                            <div className="p-2 sm:p-3 bg-blue-50 rounded-lg flex-shrink-0">
                                <AlertCircle className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <h3 className="text-base sm:text-lg font-semibold text-gray-900 leading-tight">
                                    {t("profileCompletion.title")}
                                </h3>
                                <p className="text-xs sm:text-sm text-gray-600 mt-1">
                                    {t("profileCompletion.subtitle")}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0 ml-2"
                        >
                            <X className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="mb-4 sm:mb-5 md:mb-6">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4 mb-3 sm:mb-4">
                            <div className="flex items-start gap-2 sm:gap-3">
                                <User className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                                <div className="min-w-0 flex-1">
                                    <h4 className="font-medium text-blue-900 mb-1 text-sm sm:text-base">
                                        {t("profileCompletion.profileInfoMissing")}
                                    </h4>
                                    <p className="text-xs sm:text-sm text-blue-700 leading-relaxed">
                                        {t("profileCompletion.completeBeforeAction")}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-2 sm:space-y-3">
                            <div className="flex items-start gap-2 sm:gap-3 text-xs sm:text-sm text-gray-600">
                                <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-blue-500 rounded-full mt-1.5 sm:mt-2 flex-shrink-0"></div>
                                <span className="leading-relaxed">{t("profileCompletion.addPhoneNumber")}</span>
                            </div>
                            <div className="flex items-start gap-2 sm:gap-3 text-xs sm:text-sm text-gray-600">
                                <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-blue-500 rounded-full mt-1.5 sm:mt-2 flex-shrink-0"></div>
                                <span className="leading-relaxed">{t("profileCompletion.completeProfessionalInfo")}</span>
                            </div>
                            <div className="flex items-start gap-2 sm:gap-3 text-xs sm:text-sm text-gray-600">
                                <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-blue-500 rounded-full mt-1.5 sm:mt-2 flex-shrink-0"></div>
                                <span className="leading-relaxed">{t("profileCompletion.verifyContactDetails")}</span>
                            </div>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                        <Button
                            variant="outline"
                            onClick={onClose}
                            className="flex-1 text-xs sm:text-sm py-2 sm:py-3"
                        >
                            {t("profileCompletion.cancel")}
                        </Button>
                        <Button
                            onClick={onGoToProfile}
                            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-xs sm:text-sm py-2 sm:py-3"
                        >
                            {t("profileCompletion.goToProfile")}
                        </Button>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

export default ProfileCompletionDialog;
